"use client";

import { useState, useMemo, useCallback, useReducer } from "react";
import type { HeatmapData, HeatmapDay } from "@/lib/conditions/season-heatmap";
import type { CrowdLevel } from "@/lib/conditions/crowd-prediction";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import {
  isSupProfileKey,
  sportPresentation,
} from "@/lib/conditions/sport-profiles";
import {
  ScoreConfidenceBadge,
  scoreReadingWithConfidenceRange,
} from "./ScoreConfidenceBadge";

export type HeatmapDataset = {
  key: string;
  label: string;
  data: HeatmapData;
};

type Props = {
  datasets: HeatmapDataset[];
  spotName: string;
  seasonWindow?: { start: string; end: string };
};

type Mode = "conditions" | "crowds";

type GridWeek = (HeatmapDay | null)[];
type MonthLabel = { weekIdx: number; label: string };
type Grid = { weeks: GridWeek[]; monthLabels: MonthLabel[] };

export type SeasonHeatmapInteractionState = {
  activeKey: string;
  tooltip: { datasetKey: string; day: HeatmapDay } | null;
};

export type SeasonHeatmapInteraction =
  | { type: "select-dataset"; key: string }
  | { type: "toggle-day"; day: HeatmapDay }
  | { type: "show-day"; day: HeatmapDay }
  | { type: "hide-day" };

export function createSeasonHeatmapInteractionState(
  activeKey: string,
): SeasonHeatmapInteractionState {
  return { activeKey, tooltip: null };
}

export function reduceSeasonHeatmapInteraction(
  state: SeasonHeatmapInteractionState,
  interaction: SeasonHeatmapInteraction,
): SeasonHeatmapInteractionState {
  switch (interaction.type) {
    case "select-dataset":
      return { activeKey: interaction.key, tooltip: null };
    case "toggle-day": {
      const isSelected = state.tooltip?.datasetKey === state.activeKey &&
        state.tooltip.day.date === interaction.day.date;
      return {
        ...state,
        tooltip: isSelected
          ? null
          : { datasetKey: state.activeKey, day: interaction.day },
      };
    }
    case "show-day":
      return {
        ...state,
        tooltip: { datasetKey: state.activeKey, day: interaction.day },
      };
    case "hide-day":
      return { ...state, tooltip: null };
  }
}

const CELL_SIZE = 13;
const CELL_GAP = 2;

const DAY_LABELS = ["Mon", "", "Wed", "", "Fri", "", "Sun"];

const CONDITION_COLORS: { min: number; bg: string }[] = [
  { min: 8, bg: "bg-emerald-500" },
  { min: 6, bg: "bg-lime-500" },
  { min: 4, bg: "bg-amber-500" },
  { min: 2, bg: "bg-orange-500" },
  { min: 0, bg: "bg-red-500" },
];

const CROWD_COLORS: Record<CrowdLevel, string> = {
  empty: "bg-emerald-500",
  quiet: "bg-lime-400",
  moderate: "bg-amber-400",
  busy: "bg-orange-400",
  packed: "bg-red-500",
};

function conditionColorClass(score: number | null): string {
  if (score === null) return "bg-slate-100";
  for (const { min, bg } of CONDITION_COLORS) {
    if (score >= min) return bg;
  }
  return "bg-red-500";
}

function crowdColorClass(level: CrowdLevel): string {
  return CROWD_COLORS[level];
}

function formatTooltipDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function crowdLabel(level: CrowdLevel): string {
  return level.charAt(0).toUpperCase() + level.slice(1);
}

export function heatmapConditionReading(
  day: Pick<HeatmapDay, "score" | "label" | "snowDepthConfidence">,
): string {
  if (day.score === null) return "No data";
  const label = day.label === null ? "Label unavailable" : day.label;
  const reading = scoreReadingWithConfidenceRange(
    scoreOutOf100(day.score),
    day.snowDepthConfidence?.lo,
    day.snowDepthConfidence?.hi,
  );
  return `${label} (${reading})`;
}

function buildGrid(days: HeatmapDay[]): Grid {
  if (days.length === 0) return { weeks: [], monthLabels: [] };

  const firstDate = new Date(days[0].date + "T00:00:00Z");
  const startDow = (firstDate.getUTCDay() + 6) % 7;

  const padded: (HeatmapDay | null)[] = [];
  for (let i = 0; i < startDow; i++) padded.push(null);
  for (const day of days) padded.push(day);

  const trailingNulls = (7 - (padded.length % 7)) % 7;
  for (let i = 0; i < trailingNulls; i++) padded.push(null);

  const weeks: GridWeek[] = [];
  for (let i = 0; i < padded.length; i += 7) {
    weeks.push(padded.slice(i, i + 7));
  }

  const monthLabels: MonthLabel[] = [];
  let lastMonth = "";
  for (let wIdx = 0; wIdx < weeks.length; wIdx++) {
    const firstDay = weeks[wIdx].find((d) => d !== null);
    if (!firstDay) continue;
    const d = new Date(firstDay.date + "T00:00:00Z");
    const monthKey = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
    if (monthKey !== lastMonth) {
      const label = d.toLocaleDateString("en-GB", {
        month: "short",
        timeZone: "UTC",
      });
      monthLabels.push({ weekIdx: wIdx, label });
      lastMonth = monthKey;
    }
  }

  return { weeks, monthLabels };
}

const CONDITION_LEGEND = [
  { bg: "bg-emerald-500", label: "Firing (80+)" },
  { bg: "bg-lime-500", label: "Great (60-79)" },
  { bg: "bg-amber-500", label: "Good (40-59)" },
  { bg: "bg-orange-500", label: "Fair (20-39)" },
  { bg: "bg-red-500", label: "Flat (0-19)" },
  { bg: "bg-slate-100", label: "No data" },
];

const CROWD_LEGEND = [
  { bg: "bg-emerald-500", label: "Empty" },
  { bg: "bg-lime-400", label: "Quiet" },
  { bg: "bg-amber-400", label: "Moderate" },
  { bg: "bg-orange-400", label: "Busy" },
  { bg: "bg-red-500", label: "Packed" },
];

function isInSeason(dateStr: string, window?: { start: string; end: string }): boolean {
  if (!window) return true;
  const mmdd = dateStr.slice(5);
  if (window.start <= window.end) return mmdd >= window.start && mmdd <= window.end;
  return mmdd >= window.start || mmdd <= window.end;
}

export function SeasonHeatmap({ datasets, spotName, seasonWindow }: Props) {
  const [interactionState, dispatchInteraction] = useReducer(
    reduceSeasonHeatmapInteraction,
    datasets[0]?.key ?? "",
    createSeasonHeatmapInteractionState,
  );
  const [mode, setMode] = useState<Mode>("conditions");
  const activeKey = interactionState.activeKey;

  const activeDataset = datasets.find((d) => d.key === activeKey) ?? datasets[0];
  const tooltip = interactionState.tooltip?.datasetKey === activeKey
    ? interactionState.tooltip.day
    : null;
  const grid = useMemo(
    () => buildGrid(activeDataset?.data.days ?? []),
    [activeDataset],
  );

  const handleCellClick = useCallback(
    (day: HeatmapDay) => {
      dispatchInteraction({ type: "toggle-day", day });
    },
    [],
  );

  const handleCellEnter = useCallback((day: HeatmapDay) => {
    dispatchInteraction({ type: "show-day", day });
  }, []);

  const handleCellLeave = useCallback(() => {
    dispatchInteraction({ type: "hide-day" });
  }, []);

  if (!activeDataset) return null;

  const { stats } = activeDataset.data;
  const scoredDays = activeDataset.data.days.filter((day) => day.score !== null).length;
  const dayLabelWidth = 28;

  return (
    <section id="season" className="scroll-mt-28 bg-cream">
      <div className="max-w-[1100px] mx-auto px-5 md:px-12 py-12 md:py-16">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div>
            <h2 className="font-serif text-[22px] md:text-[28px] font-bold text-navy">
              Season overview
            </h2>
            <p className="text-[13px] text-text-muted mt-1">
              Daily scores over the last 12 months at {spotName}
            </p>
            <p className="text-[12px] text-text-muted mt-1">
              {scoredDays} of {activeDataset.data.days.length} days have usable scores.
              {" "}Month comparisons need at least 80% coverage of a complete month.
            </p>
            <p className="text-[12px] text-text-muted mt-1">
              Historical quality only. Safety is not reconstructed for this view.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 min-w-0">
            {datasets.length > 1 && (
              <div className="flex flex-wrap rounded-2xl border border-border-light bg-white p-0.5">
                {datasets.map((ds) => (
                  <button
                    key={ds.key}
                    aria-label={isSupProfileKey(ds.key)
                      ? `View ${sportPresentation(ds.key).label} Beta season history`
                      : `View ${ds.label} season history`}
                    onClick={() => {
                      dispatchInteraction({ type: "select-dataset", key: ds.key });
                    }}
                    className={`min-h-11 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium transition-colors ${
                      activeKey === ds.key
                        ? "bg-navy text-white"
                        : "text-text-muted hover:text-navy"
                    }`}
                  >
                    {ds.label}
                    {isSupProfileKey(ds.key) && (
                      <span
                        aria-hidden
                        className="rounded-full border border-current/30 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.5px]"
                      >
                        Beta
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="flex rounded-full border border-border-light bg-white p-0.5">
              <button
                onClick={() => setMode("conditions")}
                className={`min-h-11 px-3 py-1 rounded-full text-[12px] font-medium transition-colors ${
                  mode === "conditions"
                    ? "bg-navy text-white"
                    : "text-text-muted hover:text-navy"
                }`}
              >
                Conditions
              </button>
              <button
                onClick={() => setMode("crowds")}
                className={`min-h-11 px-3 py-1 rounded-full text-[12px] font-medium transition-colors ${
                  mode === "crowds"
                    ? "bg-navy text-white"
                    : "text-text-muted hover:text-navy"
                }`}
              >
                Crowds
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto -mx-5 px-5 md:mx-0 md:px-0">
          <div className="inline-flex">
            <div
              className="shrink-0 pt-5"
              style={{ width: dayLabelWidth }}
            >
              {DAY_LABELS.map((label, i) => (
                <div
                  key={i}
                  className="text-[9px] text-text-muted leading-none"
                  style={{
                    height: CELL_SIZE + (i < 6 ? CELL_GAP : 0),
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {label}
                </div>
              ))}
            </div>

            <div>
              <div
                className="flex"
                style={{
                  gap: CELL_GAP,
                  marginBottom: 2,
                  height: 16,
                }}
              >
                {grid.weeks.map((_, wIdx) => {
                  const monthLabel = grid.monthLabels.find(
                    (m) => m.weekIdx === wIdx,
                  );
                  return (
                    <div
                      key={wIdx}
                      className="text-[9px] text-text-muted leading-none"
                      style={{
                        width: CELL_SIZE,
                        display: "flex",
                        alignItems: "flex-end",
                      }}
                    >
                      {monthLabel?.label ?? ""}
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateRows: `repeat(7, ${CELL_SIZE}px)`,
                  gridAutoFlow: "column",
                  gridAutoColumns: `${CELL_SIZE}px`,
                  gap: CELL_GAP,
                }}
              >
                {grid.weeks.flatMap((week, wIdx) =>
                  week.map((day, dIdx) => {
                    if (!day) {
                      return (
                        <div
                          key={`${wIdx}-${dIdx}`}
                          style={{
                            width: CELL_SIZE,
                            height: CELL_SIZE,
                          }}
                        />
                      );
                    }

                    const offSeason = !isInSeason(day.date, seasonWindow);
                    const bg =
                      mode === "conditions"
                        ? conditionColorClass(day.score)
                        : crowdColorClass(day.crowdLevel);

                    const isActive = tooltip?.date === day.date;

                    return (
                      <button
                        key={day.date}
                        className={`relative rounded-[3px] hover:ring-1 hover:ring-navy/40 transition-shadow ${bg} ${
                          isActive ? "ring-1 ring-navy" : ""
                        } ${offSeason ? "opacity-25" : ""}`}
                        style={{
                          width: CELL_SIZE,
                          height: CELL_SIZE,
                        }}
                        onClick={() => handleCellClick(day)}
                        onMouseEnter={() => handleCellEnter(day)}
                        onMouseLeave={handleCellLeave}
                        aria-label={`${day.date}: ${mode === "conditions" ? heatmapConditionReading(day) : crowdLabel(day.crowdLevel)}`}
                      >
                        {mode === "conditions" &&
                          day.snowDepthConfidence &&
                          day.snowDepthConfidence.confidence !== "full" && (
                            <span
                              aria-hidden
                              className={`absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full ring-1 ring-white ${
                                day.snowDepthConfidence.confidence === "partial"
                                  ? "bg-amber-300"
                                  : day.snowDepthConfidence.confidence === "inferred"
                                    ? "bg-slate-500"
                                    : "bg-rose-400"
                              }`}
                            />
                          )}
                      </button>
                    );
                  }),
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-4 justify-center">
          {(mode === "conditions" ? CONDITION_LEGEND : CROWD_LEGEND).map(
            (item) => (
              <div key={item.label} className="flex items-center gap-1.5">
                <span className={`w-3 h-3 rounded-[3px] ${item.bg}`} />
                <span className="text-[10px] text-text-muted">{item.label}</span>
              </div>
            ),
          )}
        </div>

        {tooltip && (
          <div className="mt-4 bg-white rounded-[12px] border border-border-light p-4 shadow-[0_4px_16px_rgba(0,0,0,0.06)] animate-fade-in">
            <div className="flex items-center justify-between gap-3">
              <span className="font-serif text-[16px] font-semibold text-navy">
                {formatTooltipDate(tooltip.date)}
              </span>
              <div className="flex items-center gap-2">
                {tooltip.score !== null && (
                  <span
                    className={`px-2 py-0.5 rounded text-[11px] font-semibold text-white ${conditionColorClass(tooltip.score)}`}
                  >
                    {heatmapConditionReading(tooltip)}
                  </span>
                )}
                {tooltip.snowDepthConfidence && (
                  <ScoreConfidenceBadge {...tooltip.snowDepthConfidence} />
                )}
                <span
                  className={`px-2 py-0.5 rounded text-[11px] font-semibold text-white ${crowdColorClass(tooltip.crowdLevel)}`}
                >
                  {crowdLabel(tooltip.crowdLevel)}
                </span>
              </div>
            </div>
            <div className="text-[11px] text-text-muted mt-1.5">
              Source: {tooltip.source === "actual" ? "Modelled history" : "No data"}
            </div>
          </div>
        )}

        {mode === "conditions" && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
            <StatCard
              label="Best months"
              value={stats.bestMonths.join(", ") || "N/A"}
            />
            <StatCard
              label="Worst months"
              value={stats.worstMonths.join(", ") || "N/A"}
            />
            <StatCard
              label="Firing / Epic days"
              value={String(stats.firingDays)}
              sublabel="Score 80+ out of 100"
            />
            <StatCard
              label="Great days"
              value={String(stats.greatDays)}
              sublabel="Score 60+ out of 100"
            />
            <StatCard
              label="Flat days"
              value={String(stats.flatDays)}
              sublabel="Score below 20 out of 100"
            />
            <StatCard
              label="Available-day average"
              value={scoredDays > 0 && stats.avgScore != null
                ? String(scoreOutOf100(stats.avgScore))
                : "N/A"}
              sublabel="Out of 100"
            />
          </div>
        )}
      </div>
    </section>
  );
}

function StatCard({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: string;
  sublabel?: string;
}) {
  return (
    <div className="bg-white rounded-[12px] border border-border-light p-4">
      <div className="text-[11px] text-text-muted font-medium">{label}</div>
      <div className="font-serif text-[18px] font-bold text-navy mt-1">
        {value}
      </div>
      {sublabel && (
        <div className="text-[11px] text-text-muted mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}
